<script setup lang="ts">
import { defineEmits } from "vue";
import { storeToRefs } from "pinia";

import "@maersk-global/mds-components-core-input-date";
import "@maersk-global/mds-components-core-icon";
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import { useScheduleSearchFilter } from "@/store/common/drawer_store";

import DateRangePicker from "@/components/schedule/DateRangeSelection.vue";

import { useGlobalStore } from "@/store/global";

const globalStore = useGlobalStore();
const emit = defineEmits(["applyHandler", "clearHandler"]);
// Drawer toggle state
const filterStore = useScheduleSearchFilter();

const {
  scheduleArrivalFromDate,
  scheduleArrivalToDate,
  actualArrivalFromDate,
  actualArrivalToDate,
  actualDepartureFromDate,
  actualDepartureToDate,
  cargoCutoffFromDate,
  cargoCutoffToDate,
  firstAvailableFromDate,
  firstAvailableToDate,
  hazardousCutoffFromDate,
  hazardousCutoffToDate,
  isDrawerEnabled,
  reeferCutoffFromDate,
  reeferCutoffToDate,
  scheduleDepartureFromDate,
  scheduleDepartureToDate,
  startReceivingFromDate,
  startReceivingToDate,
  vesselName,
  vesselStatus,
  vesselBerth
} = storeToRefs(filterStore);

const labels = {
  vesselName: globalStore.sitecoreData.table_columns.schedule.vesselName,
  vesselStatus: globalStore.sitecoreData.table_columns.schedule.vesselStatus,
  scheduleArrivalDatetime: globalStore.sitecoreData.table_columns.schedule.scheduledArrivalDateTimeLocal,
  arrivalDateTime: globalStore.sitecoreData.table_columns.schedule.estimatedArrivalDateTimeLocal,
  firstAvailableDatetime: globalStore.sitecoreData.table_columns.schedule.firstAvailableDateTimeLocal,
  scheduleDeparture: globalStore.sitecoreData.table_columns.schedule.scheduledDepartureDateTimeLocal,
  departureDatetime: globalStore.sitecoreData.table_columns.schedule.actualDepartureDateTimeLocal,
  startReceiveDateTime: globalStore.sitecoreData.table_columns.schedule.startReceiveDateTimeLocal,
  cargoCutoffDateTime: globalStore.sitecoreData.table_columns.schedule.cargoCutoffDateTimeLocal,
  reeferCutoffDateTime: globalStore.sitecoreData.table_columns.schedule.reeferCutoffDateTimeLocal,
  hazardousCutoffDateTime: globalStore.sitecoreData.table_columns.schedule.hazardousCutoffDateTimeLocal,
  berth: globalStore.sitecoreData.table_columns.schedule.berthName,
  from: globalStore.sitecoreData.common.from,
  to: globalStore.sitecoreData.common.to,
  apply: globalStore.sitecoreData.cta.apply,
  clear: globalStore.sitecoreData.cta.clear,
  filters: globalStore.sitecoreData.headings.filters,
  searchVesselName: globalStore.sitecoreData.placeHolders.searchVesselName,
  searchVesselStatus: globalStore.sitecoreData.placeHolders.searchVesselStatus,
  searchBerth: globalStore.sitecoreData.placeHolders.searchBerth
};

const scheduleFilter = (): void => {
  filterStore.toggleDrawer();
};

// Filter button event to filter appointment
const filterAppointment = (): void => {
  filterStore.toggleDrawer();
  emit("applyHandler");
};

const clearFilter = (): void => {
  filterStore.clearFilters();
  emit("clearHandler");
  filterStore.toggleDrawer();
};
</script>

<template>
  <mc-drawer :open="isDrawerEnabled" position="right" customsize="20%" @closed="scheduleFilter()">
    <span slot="heading">{{ labels.filters }}</span>
    <div class="content">
      <div class="date-picker-filter">
        <div>{{ labels.vesselName }}</div>
        <mc-input
          :value="vesselName"
          icon="magnifying-glass"
          label=""
          :placeholder="labels.searchVesselName"
          name="vesselName"
          fit="small"
          @input="filterStore.setVesselName($event.target.value)"
          @clearbuttonclick="filterStore.setVesselName($event.target.value)"
        >
        </mc-input>
      </div>

      <div class="date-picker-filter">
        <div>{{ labels.vesselStatus }}</div>
        <mc-input
          :value="vesselStatus"
          icon="magnifying-glass"
          fit="small"
          label=""
          :placeholder="labels.searchVesselStatus"
          name="status"
          @input="filterStore.setVesselStatus($event.target.value)"
          @clearbuttonclick="filterStore.setVesselName($event.target.value)"
        >
        </mc-input>
      </div>

      <div class="date-picker-filter">
        <div>{{ labels.berth }}</div>
        <mc-input
          :value="vesselBerth"
          fit="small"
          icon="magnifying-glass"
          label=""
          :placeholder="labels.searchBerth"
          name="berth"
          @input="filterStore.setVesselBerth($event.target.value)"
          @clearbuttonclick="filterStore.setVesselBerth($event.target.value)"
        >
        </mc-input>
      </div>

      <!-- Schedule arrival -->
      <DateRangePicker
        :label="labels.scheduleArrivalDatetime"
        :valueFrom="scheduleArrivalFromDate"
        :valueTo="scheduleArrivalToDate"
        @from-date="filterStore.setScheduleArrivalFromDate($event.detail)"
        @to-date="filterStore.setScheduleArrivalToDate($event.detail)"
      />

      <!-- Actual arrival date time-->
      <DateRangePicker
        :label="labels.arrivalDateTime"
        :valueFrom="actualArrivalFromDate"
        :valueTo="actualArrivalToDate"
        @from-date="filterStore.setActualArrivalFromDate($event.detail)"
        @to-date="filterStore.setActualArrivalToDate($event.detail)"
      />

      <!-- First receive date time-->
      <DateRangePicker
        :label="labels.firstAvailableDatetime"
        :valueFrom="firstAvailableFromDate"
        :valueTo="firstAvailableToDate"
        @from-date="filterStore.setFirstAvailableFromDate($event.detail)"
        @to-date="filterStore.setFirstAvailableToDate($event.detail)"
      />

      <!-- Schedul daparture date time-->
      <DateRangePicker
        :label="labels.scheduleDeparture"
        :valueFrom="scheduleDepartureFromDate"
        :valueTo="scheduleDepartureToDate"
        @from-date="filterStore.setScheduleDepartureFromDate($event.detail)"
        @to-date="filterStore.setScheduleDepartureToDate($event.detail)"
      />

      <!-- Actual departure date time-->
      <DateRangePicker
        :label="labels.departureDatetime"
        :valueFrom="actualDepartureFromDate"
        :valueTo="actualDepartureToDate"
        @from-date="filterStore.setActualDepartureFromDate($event.detail)"
        @to-date="filterStore.setActualDepartureToDate($event.detail)"
      />

      <!-- Start receive date time-->
      <DateRangePicker
        :label="labels.startReceiveDateTime"
        :valueFrom="startReceivingFromDate"
        :valueTo="startReceivingToDate"
        @from-date="filterStore.setStartReceivingFromDate($event.detail)"
        @to-date="filterStore.setStartReceivingToDate($event.detail)"
      />

      <!-- Cargo cutoff date time-->
      <DateRangePicker
        :label="labels.cargoCutoffDateTime"
        :valueFrom="cargoCutoffFromDate"
        :valueTo="cargoCutoffToDate"
        @from-date="filterStore.setCargoCutoffFromDate($event.detail)"
        @to-date="filterStore.setCargoCutoffToDate($event.detail)"
      />

      <!-- Reefer cutoff date time-->
      <DateRangePicker
        :label="labels.reeferCutoffDateTime"
        :valueFrom="reeferCutoffFromDate"
        :valueTo="reeferCutoffToDate"
        @from-date="filterStore.setReeferCutoffFromDate($event.detail)"
        @to-date="filterStore.setReeferCutoffToDate($event.detail)"
      />

      <!-- Hazardous cutoff date time-->
      <DateRangePicker
        :label="labels.hazardousCutoffDateTime"
        :valueFrom="hazardousCutoffFromDate"
        :valueTo="hazardousCutoffToDate"
        @from-date="filterStore.setHazardousCutoffFromDate($event.detail)"
        @to-date="filterStore.setHazardousCutoffToDate($event.detail)"
      />
    </div>
    <div slot="footer" class="drawer-footer-button">
      <mc-button width="full-width" fit="medium" appearance="neutral" @click="clearFilter()">{{
        labels.clear
      }}</mc-button>
      <div class="spacer"></div>
      <mc-button width="full-width" fit="medium" @click="filterAppointment()">{{ labels.apply }}</mc-button>
    </div>
  </mc-drawer>
</template>
